import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Card,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useInView } from "react-intersection-observer";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const OfferCard = ({ index, title, imageUrl, description, redirectPath }) => {

  const history = useHistory();
  // Use the above as props
  const isEven = index % 2 === 0;
  const isDesktop = useMediaQuery("(min-width:600px)");

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const animationVariants = {
    hidden: {
      opacity: 0,
      y: isEven ? 50 : -50,
      transition: {
        type: "spring",
        damping: 20, // Spring Smoothness
        stiffness: 300, // Spring Stiffness(high value less stiff)
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        ease: "easeInOut",
        duration: 1,
        delay: index / 10,
        when: "beforeChildren", // Determines when to apply the transition
        staggerChildren: 0.1, // Delay in animating children
      },
    },
    exit: {
      opacity: 0,
      y: isEven ? -50 : 50,
      transition: {
        ease: "easeInOut",
        duration: 0.5,
      },
    },
  };

  return (
    <motion.div
    ref={ref}
    variants={isDesktop ? animationVariants : null}
    initial="hidden"
    animate={inView ? "visible" : "hidden"}
    >
      <Card
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: 390, sm: 430, md: 470 },
          cursor: "pointer",
          overflow: "hidden",
          borderRadius: 0,
          boxShadow: "0 12px 34px rgba(41,39,32,0.12)",
          backgroundColor: "#292720",
          transition: "box-shadow 280ms ease, transform 280ms ease",
          "&:hover": {
            boxShadow: "0 20px 48px rgba(41,39,32,0.2)",
            "& .offer-card-image": { transform: "scale(1.055)" },
            "& .offer-card-arrow": {
              color: "#292720",
              backgroundColor: "#d5bd91",
              borderColor: "#d5bd91",
              transform: "translate(2px, -2px)",
            },
          },
          "&:focus-visible": {
            outline: "2px solid #a68757",
            outlineOffset: 4,
          },
        }}
        onClick={() => history.push(redirectPath)}
        role="link"
        aria-label={`Explore ${title}`}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            history.push(redirectPath);
          }
        }}
      >
        <Box
          component="img"
          className="offer-card-image"
          src={imageUrl}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 700ms ease",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(22,21,18,0.04) 20%, rgba(22,21,18,0.28) 48%, rgba(22,21,18,0.92) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 22,
            left: 24,
            color: "#f3dfbd",
            fontSize: "0.68rem",
            fontWeight: 600,
            letterSpacing: "0.14em",
          }}
        >
          0{index + 1} <Box component="span" sx={{ color: "rgba(243,223,189,0.72)" }}>/ HOMELY
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            right: 22,
            bottom: 24,
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            color: "#f6f4ef",
            border: "1px solid rgba(246,244,239,0.7)",
            borderRadius: "50%",
            transition:
              "transform 220ms ease, color 220ms ease, background-color 220ms ease, border-color 220ms ease",
          }}
          className="offer-card-arrow"
          aria-hidden="true"
        >
          <ArrowOutwardIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: { xs: 3, md: 4 },
            right: { xs: 3, md: 4 },
            bottom: { xs: 3.5, md: 4 },
            color: "#f6f4ef",
            pr: 7,
          }}
        >
          <Typography
            component="h3"
            sx={{
              mb: 1.2,
              color: "#f6f4ef",
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: { xs: "1.9rem", md: "2.35rem" },
              lineHeight: 1.12,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              maxWidth: 480,
              color: "rgba(246,244,239,0.8)",
              fontSize: { xs: "0.82rem", md: "0.88rem" },
              lineHeight: 1.75,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        </Box>
      </Card>
    </motion.div>
  );
};

export default OfferCard;
